import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import ReactDOM from "react-dom/client";
import ReactGA from "react-ga4";
import { routeTree } from "./routeTree.gen";
import "./index.css";
import { MenuProvider } from "@/services/context/MobileMenuContext";
import React from "react";

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: false,
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000, // 5 minutos
        },
    },
});

// Set up a Router instance
const router = createRouter({
    routeTree,
    context: {
        queryClient,
    },
    defaultPreload: false,
    // Since we're using React Query, we don't want loader calls to ever be stale
    // This will ensure that the loader is always called when the route is preloaded or visited
    defaultPreloadStaleTime: 0,
});

const CHUNK_RELOAD_KEY = "maifud-chunk-reload-attempted";

const isChunkLoadError = (error: Error) => {
    const errorText = `${error.name} ${error.message}`;

    return (
        errorText.includes("Failed to fetch dynamically imported module") ||
        errorText.includes("Importing a module script failed") ||
        errorText.includes("valid JavaScript MIME type") ||
        errorText.includes("ChunkLoadError")
    );
};

const handleChunkLoadError = (event: ErrorEvent | PromiseRejectionEvent) => {
    const reason = "reason" in event ? event.reason : event.error;
    const error = reason instanceof Error ? reason : new Error(String(reason));

    if (!isChunkLoadError(error)) {
        return;
    }

    Sentry.captureException(error);

    if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === "true") {
        return;
    }

    sessionStorage.setItem(CHUNK_RELOAD_KEY, "true");
    window.location.reload();
};

window.addEventListener("error", handleChunkLoadError);
window.addEventListener("unhandledrejection", handleChunkLoadError);

window.addEventListener("load", () => {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
});

// Register things for typesafety
declare module "@tanstack/react-router" {
    interface Register {
        router: typeof router;
    }
}

const rootElement = document.getElementById("root");
if (rootElement) {
    const root = ReactDOM.createRoot(rootElement);
    root.render(
        <React.StrictMode>
            <QueryClientProvider client={queryClient}>
                <MenuProvider>
                    <RouterProvider router={router} />
                </MenuProvider>
            </QueryClientProvider>
        </React.StrictMode>,
    );
} else {
    Sentry.captureException(
        new Error("No se encontró el elemento raíz con id 'root'"),
    );
}

// Avoid sentry in localhost
if (!window.location.host.includes("localhost")) {
    // Start Google Analytics
    ReactGA.initialize("G-J0Q77MPKEF");
    router.history.subscribe(() => {
        const pathname = router.history.location.pathname;
        ReactGA.send({ hitType: "pageview", page: pathname });
    });

    // Start Sentry
    Sentry.init({
        dsn: "https://2ae7a4683bbf520cf9702ca91811d313@o4505331551895552.ingest.us.sentry.io/4506865890557952",
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
            }),
            Sentry.tanstackRouterBrowserTracingIntegration(router),
        ],
        // Performance Monitoring
        tracesSampleRate: 1.0, //  Capture 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ["localhost"],
        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
}
